import {
  Button,
  Chip,
  Download,
  SvgExport,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CONTRACT_AIQ_INTERNAL,
  DRAFT_SELECTED,
  EXPERICENCE_SCREEN,
  PLAN_DOCUMENTS_INTERNAL,
} from '../routes';
import moment from 'moment';
import axios from 'axios';
import properties from '../properties';
import contractAIQConstants from '../messages/ContractAIQConstants';
import { formatEmployerGroupLocation } from './homePageHelper';
import { DottedText } from './financialForensicsHelper';

type MetLevel = 'Fully Met' | 'Not Met' | '';

export const getChipComponent = (
  chipType: MetLevel,
  label?: string | React.JSX.Element,
  width?: string,
) => {
  switch (chipType) {
    case 'Fully Met':
      return (
        <Chip
          label={
            <Typography
              fontSize="12px"
              lineHeight="15px"
              fontType="medium"
              textColor="#17A39C"
            >
              {label ?? 'Fully Met'}
            </Typography>
          }
          backgroundColor="#08C7B41A"
          border="0.3px solid #17A39C"
          borderRadius="4px"
          padding="5px 8px"
          height="25px"
          width={width || (label ? '120px' : '150px')}
        />
      );
    case 'Not Met':
      return (
        <Chip
          label={
            <Typography
              fontSize="12px"
              lineHeight="15px"
              fontType="medium"
              textColor="#D46A6D"
            >
              {label ?? 'Not Met'}
            </Typography>
          }
          backgroundColor="#FBEEEE"
          border="0.3px solid #D46A6D"
          borderRadius="4px"
          padding="5px 8px"
          height="25px"
          width={width || (label ? '120px' : '150px')}
        />
      );
    default:
      return (
        <Chip
          label={
            <Typography
              fontSize="12px"
              lineHeight="15px"
              fontType="medium"
              textColor={patientAccessTheme.palette.grey[900]}
            >
              {label ?? 'NA'}
            </Typography>
          }
          backgroundColor={patientAccessTheme.palette.grey[300]}
          border={'0.3px solid' + patientAccessTheme.palette.grey[900]}
          borderRadius="4px"
          padding="5px 8px"
          height="25px"
          width={width || (label ? '120px' : '150px')}
        />
      );
  }
};

export const ClauseDetailsFormatter = (data: any) => {
  let metCount = 0;
  let unMetCount = 0;
  const rowData: any = [];
  data?.check_off_list_id?.forEach((item: any) => {
    if (item.flag === 'Yes') {
      metCount++;
      rowData.push([
        item.category,
        item.parameter,
        getChipComponent('Fully Met'),
      ]);
    } else if (item.flag === 'No') {
      unMetCount++;
      rowData.push([
        item.category,
        item.parameter,
        getChipComponent('Not Met'),
      ]);
    }
  });
  return {
    metCount,
    unMetCount,
    rowData,
  };
};

export const getBarColor = (value: number) => {
  if (value < 40) {
    return `#D6545E`;
  } else if (value >= 40 && value < 80) {
    return `#F5C26B`;
  } else {
    return `#17A39C`;
  }
};

export const getRiskCategory = (value: number) => {
  if (value < 40) {
    return 'High';
  } else if (value >= 40 && value < 80) {
    return 'Medium';
  } else {
    return 'Low';
  }
};

//Title and SubTitle component for clauses/regulations
export const TitleAndSubTitle = (title: string, subTitle: string) => {
  return (
    <>
      <Typography
        fontSize="18px"
        fontType="medium"
        lineHeight="21.78px"
        textColor={patientAccessTheme.palette.text.highemphasis}
        margin="0px 0px 8px 0px"
      >
        {title}
      </Typography>
      <Typography
        fontSize="14px"
        fontType="regular"
        lineHeight="24px"
        textColor={patientAccessTheme.palette.text.highemphasis}
      >
        {subTitle}
      </Typography>
    </>
  );
};

export const getReportBreadcrumbList = (
  navigate: ReturnType<typeof useNavigate>,
  state: any,
  isDraft?: boolean,
) => {
  const {
    reportName,
    document,
    healthcarePartner,
    contract,
    draftId,
    draft,
    isExperience = false,
    selectedTab,
  }: any = state || {};

  const breadcrumbsList: any[] = [
    [
      'text',
      document,
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
        maxWidth: '325px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      () => {
        isExperience
          ? navigate(EXPERICENCE_SCREEN, {
              state: {
                contract,
                healthcarePartner,
                contractId: draftId, // or any other necessary state
              },
            })
          : isDraft
          ? navigate(DRAFT_SELECTED, { state: { row: draft } })
          : navigate(CONTRACT_AIQ_INTERNAL, {
              state: {
                healthcarePartner,
                contract,
                selectedTab,
              },
            });
      },
    ],
  ];

  // ✅ Only add second breadcrumb if not from Experience
  if (!isExperience) {
    breadcrumbsList.push([
      'text',
      isDraft ? 'Draft Analysis' : 'Compliance Analysis',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        isDraft
          ? navigate(DRAFT_SELECTED, {
              state: { row: draft, isReportsTab: true },
            })
          : navigate(CONTRACT_AIQ_INTERNAL, {
              state: {
                isReportsTab: true,
                healthcarePartner,
                contract,
                selectedTab,
              },
            });
      },
    ]);
  }

  // Always add the last breadcrumb
  breadcrumbsList.push([
    'text',
    reportName,
    {
      color: patientAccessTheme.palette.text.highemphasis,
      fontWeight: 600,
    },
  ]);

  return breadcrumbsList;
};

export const getSOBReportBreadcrumbList = (
  navigate: ReturnType<typeof useNavigate>,
  state: any,
) => {
  const {
    reportName,
    document,
    tabIndex,
    planDocId,
    isExperience = false,
  }: any = state || {};

  const breadcrumbsList: any[] = [
    [
      'text',
      document,
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        isExperience
          ? navigate(EXPERICENCE_SCREEN)
          : navigate(PLAN_DOCUMENTS_INTERNAL, {
              state: { planId: planDocId },
            });
      },
    ],
  ];

  // ✅ Add the 'Reports' breadcrumb only if not from Experience
  if (!isExperience) {
    breadcrumbsList.push([
      'text',
      'Reports',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        navigate(PLAN_DOCUMENTS_INTERNAL, {
          state: {
            tabIndex,
            planId: planDocId,
          },
        });
      },
    ]);
  }

  // Always add the final breadcrumb
  breadcrumbsList.push([
    'text',
    reportName,
    {
      color: patientAccessTheme.palette.text.highemphasis,
      fontWeight: 600,
    },
  ]);

  return breadcrumbsList;
};

export const getStandardRiskValues = (standard: any) => {
  let highRiskCount = 0;
  let mediumRiskCount = 0;
  let lowRiskCount = 0;
  standard?.clauses?.map((item: any) => {
    switch (getRiskCategory(item?.overallScore)) {
      case 'High':
        highRiskCount += 1;
        break;
      case 'Medium':
        mediumRiskCount += 1;
        break;
      case 'Low':
        lowRiskCount += 1;
        break;
      default:
        break;
    }
  });
  const values = [
    { value: highRiskCount },
    { value: mediumRiskCount },
    { value: lowRiskCount },
  ];

  return values;
};

export const getReportStatusText = (status: string, reportName?: string) => {
  switch (status) {
    case 'Processing':
      return 'Processing Report';
    case 'Ready':
      return reportName === 'Commercial Evaluation' ||
        reportName === 'Comprehensive Report'
        ? 'Download Report'
        : 'Ready for Review';
    case 'Notshared':
      return 'Contract Not Shared';
    case 'NotSelected':
      return 'Report Not Selected';
    default:
      return '';
  }
};

export interface OrganizationTypes {
  'Claims Administrator': string;
  PBM: string;
  'Stop loss carrier': string;
  'Network Carrier': string;
  'Broker/ Advisor/ Consultant': string;
}

export interface ComplianceAssuranceType {
  id: string;
  eg_nid: string;
  group: string;
  employees: number;
  planType: string;
  renewalMonth: string;
  location: string;
  status: 'Ready' | 'In Progress' | 'Processing' | 'No Documents Uploaded';
}

export const formatComplianceAssuranceData = (data: any) => {
  const groups: ComplianceAssuranceType[] = data?.map((item: any) => {
    return {
      id: item['_id'],
      group: item?.employer_name,
      employees: item?.employees || '-',
      planType: item?.plan_type || '-',
      renewalMonth: item?.renewal_month || '-',
      location: formatEmployerGroupLocation(item?.location || '-'),
      status: item?.status,
      eg_nid: item?.eg_nid,
    };
  });

  return groups;
};

export const bytesToMB = (bytes: number) => {
  const MB = (bytes / (1024 * 1024))?.toFixed(2);
  return MB;
};

export const getReportsByServiceAgreement = (
  service_agreement_name: string,
) => {
  switch (service_agreement_name) {
    case 'Third-Party Administrator (TPA)':
    case 'Administrative Services Only (ASO) Provider':
      return [
        // 'Purchasing Standards Compliance Review',
        'Compensation Transparency Compliance Check',
        'Gag Clause Prohibition Compliance Check',
        'Commercial Evaluation',
        // 'Comparison with Summary Plan Description',
        // 'Comparison with PBM Service Agreement',
        // 'Comparison with Network Carrier Agreement',
      ];
    case 'Pharmacy Benefit Manager (PBM)':
    case 'Provider Network Access':
      return [
        // 'Purchasing Standards Compliance Review',
        'Compensation Transparency Compliance Check',
        'Gag Clause Prohibition Compliance Check',
        // 'Comparison with Summary Plan Description',
        // 'Comparison with Administrative Service Agreement',
      ];
    case 'Broker or Consultant':
      return ['Compensation Transparency Compliance Check'];
    case 'Stop-Loss Insurance Provider':
      return [
        // 'Comparison with Administrative Service Agreement'
      ];
    case 'Direct Contract Provider (or Group)':
      return ['Gag Clause Prohibition Compliance Check'];
    default:
      return [];
  }
};

export const getFeaturesByServiceAgreement = (
  service_agreement_name: string,
) => {
  switch (service_agreement_name) {
    case 'Third-Party Administrator (TPA)':
    case 'Administrative Services Only (ASO) Provider':
      return [
        'Fees',
        // 'Key Terms Compilation',
        // 'Red Flags Check',
        // 'Missing Checks',
        'Summary of Findings',
      ];
    case 'Pharmacy Benefit Manager (PBM)':
      return [
        // 'Red Flags Check',
        //  'Missing Checks',
        // 'Key Terms Compilation'
      ];
    case 'Provider Network Access':
      return [
        // 'Red Flags Check',
        // 'Missing Checks'
      ];
    case 'Broker or Consultant':
      return [
        // 'Red Flags Check',
        // 'Missing Checks'
      ];
    case 'Stop-Loss Insurance Provider':
      return [
        'Key Terms Compilation',
        // 'Red Flags Check',
        // 'Missing Checks'
      ];
    default:
      return [];
  }
};

export const formatPlanDocsData = (
  dataArray: any[],
  uploadedDocuments?: any[],
) => {
  const data = dataArray
    .map((dataItem) => {
      const formattedPlans = dataItem.plans.map((plan: any) => {
        const subrows = plan.documents.map((doc: any) => ({
          id: doc?._id,
          document: doc.document_name || '',
          documentType: doc.document_type || '',
          totalFiles: '-',
          effectiveDate: '',
          background: uploadedDocuments?.some(
            (uploadDoc: any) => uploadDoc?.document_id === doc?._id,
          )
            ? '#fff9cc'
            : patientAccessTheme.palette.grey[100],
        }));

        return {
          plan_id: plan._id,
          name: plan.plan_type,
          type: dataItem.doc_type,
          totalFiles: plan.documents.length,
          effectiveDate: `${effectiveDateFormat(
            plan.plan_start_date,
          )} - ${effectiveDateFormat(plan.plan_end_date)}`,
          subrows: subrows,
        };
      });
      return formattedPlans;
    })
    .flat(); // Flatten the array so all plans are in one array
  return data;
};

export const formatMergePlanDocsData = (
  dataArray: any[],
  documentMergerAction?: (plan_id: string, isPlanDoc: boolean) => void,
  documentDownloadAction?: (plan_id: string, isPlanDoc: boolean) => void,
  masterDocumentUploadAction?: (
    plan_id: string,
    docId: string,
    isPlanDoc: boolean,
    item: any,
  ) => void,
) => {
  let contractWithAmendmentsCount: number = 0;
  const data = dataArray
    ?.map((plan: any) => {
      let baseDocId: string;
      if (
        plan &&
        plan?.type?.toLowerCase().includes('amendment') &&
        plan?.merge_status === 'Not Merged'
      ) {
        contractWithAmendmentsCount++;
      }

      const subrows = plan.documents.map((doc: any, docIndex: number) => {
        if (doc.document_type === 'Base Document') {
          baseDocId = doc._id;
        }
        return {
          id: docIndex,
          document: doc.document_name || '',
          documentType: doc.document_type || '',
        };
      });
      return {
        plan_id: plan._id,
        benefitTypes:
          plan?.benefit_types && plan.benefit_types.length > 0
            ? plan.benefit_types
            : [],
        name: plan.plan_names,
        type: plan.document_type,
        subrows: subrows,
        document_type: plan.type,
        status: (
          <Typography
            style={{
              width: '120px',
              paddingLeft: '10px',
              color: plan.merge_status === 'Not Merged' ? '#E08083' : '#17A39C',
            }}
          >
            {plan.merge_status}
          </Typography>
        ),
        is_document_converted: plan?.is_document_converted,
        document_merger: (
          <Button
            label="Document Merger"
            variant="outlined"
            color="primary"
            height="33px"
            width="170px"
            borderRadius="4px"
            onClick={() => {
              documentMergerAction && documentMergerAction(plan._id, true);
            }}
            disabled={plan?.type === 'Base Document'}
          />
        ),
        upload: (
          <SvgExport
            height="24px"
            width="24px"
            cursor={'pointer'}
            onClick={() => {
              masterDocumentUploadAction &&
                masterDocumentUploadAction(plan._id, baseDocId, true, plan);
            }}
          />
        ),
        download: (
          <Download
            stroke={patientAccessTheme.palette.status.icon}
            height="20px"
            width="24px"
            cursor={'pointer'}
            onClick={() => {
              documentDownloadAction && documentDownloadAction(plan._id, true);
            }}
          />
        ),
      };
    })
    .flat(); // Flatten the array so all plans are in one array
  return { data, contractWithAmendmentsCount };
};

export const formatReportsData = (data: any) => {
  return data?.map((item: any) => {
    const reports = getReportsByServiceAgreement(item?.service_agreement_name);
    const features = getFeaturesByServiceAgreement(
      item?.service_agreement_name,
    );

    // Allow multiple targets
    const comparisonMap: Record<string, string[]> = {
      'Third-Party Administrator (TPA)': ['Stop-Loss Insurance Provider'],
      'Administrative Services Only (ASO) Provider': [
        'Stop-Loss Insurance Provider',
      ],
      'Stop-Loss Insurance Provider': [
        'Third-Party Administrator (TPA)',
        'Administrative Services Only (ASO) Provider',
      ],
    };

    const reportMap: Record<string, string> = {
      'Third-Party Administrator (TPA)': 'Comparison with Stop Loss Agreement',
      'Administrative Services Only (ASO) Provider':
        'Comparison with Stop Loss Agreement',
      'Stop-Loss Insurance Provider':
        'Comparison with Administrative Service Agreement',
    };

    const targetNames = comparisonMap[item?.service_agreement_name];
    const reportToAdd = reportMap[item?.service_agreement_name];

    if (targetNames?.length && reportToAdd) {
      const targetContract = data?.find((contract: any) =>
        targetNames.includes(contract?.service_agreement_name),
      );
      if (targetContract) {
        reports?.push(reportToAdd);
      }
    }

    return {
      contract_id: item?.contract_id,
      name: item?.health_care_partner_name,
      type: item?.service_agreement_name,
      features: [...reports, ...features],
      selectedFeatures: [...(item?.reports ?? []), ...(item?.features ?? [])],
      showSave: item?.merge_status === 'Merged',
    };
  });
};

export const formatContractData = (item: any, uploadedDocuments: any) => ({
  contract_id: item?.contract_id,
  name: item?.health_care_partner_name,
  type: item?.service_agreement_name,
  totalFiles: item?.total_files,
  effectiveDate: effectiveDateFormat(item?.effective_date),
  subrows: item?.documents?.map((doc: any, index: number) => ({
    id: index,
    document: doc?.document_name,
    documentType: doc?.document_type,
    totalFiles: '-',
    effectiveDate: effectiveDateFormat(doc?.document_effective_date),
    background: uploadedDocuments?.some(
      (uploadDoc: any) => uploadDoc.document_id === doc._id,
    )
      ? '#fff9cc'
      : patientAccessTheme.palette.grey[100],
  })),
});

export const formatMergeContractData = (
  item: any,
  documentMergerAction: (contract_id: any, isPlanDoc: boolean) => void,
  documentDownloadAction?: (contract_id: any, isPlanDoc: boolean) => void,
  masterDocumentUploadAction?: (
    contract_id: string,
    docId: string,
    isPlanDoc: boolean,
    data: any,
  ) => void,
) => {
  let baseDocId: string;
  const subRows = item?.documents?.map((doc: any, index: number) => {
    if (doc.document_type === 'Base Document') {
      baseDocId = doc._id;
    }
    return {
      id: index,
      document: doc?.document_name,
      documentType: doc?.document_type,
      effectiveDate: effectiveDateFormat(doc?.document_effective_date),
      background: patientAccessTheme.palette.grey[100],
    };
  });
  return {
    contract_id: item?.contract_id,
    name: item?.health_care_partner_name,
    type: item?.service_agreement_name,
    document_type: item?.type,
    status: (
      <Typography
        style={{
          width: '120px',
          paddingLeft: '10px',
          color: item.merge_status === 'Not Merged' ? '#E08083' : '#17A39C',
        }}
      >
        {item.merge_status}
      </Typography>
    ),
    document_merger: (
      <Button
        label="Document Merger"
        variant="outlined"
        color="primary"
        height="33px"
        width="170px"
        borderRadius="4px"
        onClick={() => documentMergerAction(item.contract_id, false)}
        disabled={item?.type === 'Base Document'}
      />
    ),
    upload: (
      <SvgExport
        height="24px"
        width="24px"
        cursor={'pointer'}
        onClick={() => {
          masterDocumentUploadAction &&
            masterDocumentUploadAction(
              item.contract_id,
              baseDocId,
              false,
              item,
            );
        }}
      />
    ),
    download: (
      <Download
        stroke={patientAccessTheme.palette.status.icon}
        height="20px"
        width="24px"
        cursor={'pointer'}
        onClick={() => {
          documentDownloadAction &&
            documentDownloadAction(item.contract_id, false);
        }}
      />
    ),
    subrows: subRows,
    reports: item?.reports,
  };
};

export const formatDraftReportData = (data: any) => {
  const reportData = data.map((item: any) => {
    const reports = getReportsByServiceAgreement(
      item?.service_agreement_id?.service_agreement_name,
    );
    const features = getFeaturesByServiceAgreement(
      item?.service_agreement_id?.service_agreement_name,
    );
    return {
      draft_id: item?._id,
      name: item?.draft_name,
      type: item?.service_agreement_id?.service_agreement_name,
      status: item?.status,
      features: [...reports, ...features],
      selectedFeatures: [...(item?.reports ?? []), ...(item?.features ?? [])],
      showSave: true,
      serviceAgreementId: item?.service_agreement_id?._id,
      documentUrl: item?.document_url,
      documentName: item?.document_name,
      is_metadata_extracted: item?.is_metadata_extracted,
    };
  });
  return reportData;
};

export const formatDraftsData = (data: any) => {
  const tableData = data.map((item: any) => {
    const subRows = [
      {
        id: 0,
        document: item?.document_name,
        documentType: item?.document_type,
        background: patientAccessTheme.palette.grey[100],
      },
    ];
    return {
      draft_id: item?._id,
      name: item?.draft_name,
      type: item?.service_agreement_id?.service_agreement_name,
      serviceAgreementId: item?.service_agreement_id?._id,
      effectiveDate: moment(item?.createdAt).utc().format('MMMM D, yyyy'),
      document_type: item?.status,
      documentUrl: item?.document_url,
      subrows: subRows,
      documentName: item?.document_name,
      selectedFeatures: item?.features,
      status: item?.status,
      is_metadata_extracted: item?.is_metadata_extracted,
    };
  });
  return tableData;
};

export const effectiveDateFormat = (date: string) => {
  return date ? moment(date).format('DD MMM YYYY') : '-';
};

export const createBlobUrl = async (documentUrl: string) => {
  try {
    const response = await axios.post(
      `${properties.aiqBaseUrl}${properties.contractBaseUrl}${properties.readFile}`,
      { documentUrl },
    );
    if (response.status === 200) {
      const byteCharacters = atob(response.data?.data);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteArray], {
        type: 'application/pdf',
      });
      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    }
  } catch (error) {
    throw error;
  }
  return '';
};

export const getProvisionRangeColor = (status: any) => {
  switch (status) {
    case 'Favorable':
      return '#17A39C';
    case 'Neutral':
      return '';
    case 'Unfavorable':
      return '#D46A6D';
    default:
      return '';
  }
};

export interface MatrixData {
  purpose: string;
  clause: string;
  favorability_range: string;
  key: string;
  field_name: string;
  field_description: string;
  value: string | boolean;
  evidence?: string[];
  chunks?: any[];
  explaination: string;
  impact?: any;
  importance?: any;
  provision_name: string;
  assessment_description: string;
  category: string;
  benchmark: string;
  explanation: string;
  favorability: string;
  legal_doctorine?: string[];
  legal_citation?: string[];
  slide_topic?: string;
}

export interface Fee {
  favorability_range: string;
  benchmark: string;
  key: string;
  field_name: string;
  field_description: string;
  value: any;
  evidence: string[];
  chunks: any[];
  provision_name: string;
  assessment_description: string;
  category: string;
  explanation: string;
  favorability: string;
}

export const getProvisonCellBackground = (
  status: string,
  value: boolean | string,
) => {
  switch (status) {
    case 'Favorable':
    case 'Neutral':
      return value === true ? '#08C7B41A' : '';
    case 'Unfavorable':
      return value === true ? '#EDC0BB4D' : '';
    default:
      return '';
  }
};

export const getName = (label: string) => {
  switch (label) {
    case 'Unfavourable':
      return 'Unfavorable';
    case 'Favourable':
      return 'Favorable';
    default:
      return label;
  }
};

export const formatProvisions = (
  provisions: MatrixData[],
  compareProvisions: {
    eg_nid: string;
    provisions: MatrixData[];
  }[],
  onClickReference?: any,
) => {
  if (!provisions?.length) {
    return [];
  }

  // Map through the provisions of the current group entries
  const formattedData = provisions?.map((provision) => {
    const formattedProvision: Record<string, any> = {
      provision: provision?.field_name,
      favorabilityRange: getName(provision?.favorability_range) || 'NA',
      status: provision?.favorability_range,
      value: provision?.value,
      // tooltip: {
      //   description: provision?.field_description,
      //   explaination: provision?.explaination,
      //   value: provision?.evidence?.[0] || '',
      // },
      background: {
        value: getProvisonCellBackground(
          provision?.favorability_range,
          provision?.value,
        ),
      },
      onClickReference,
    };

    if (compareProvisions?.length) {
      // Dynamically add values from each eg_nid in allData
      compareProvisions?.forEach((data) => {
        const matchingProvision = data?.provisions?.find(
          (p) => p?.key === provision?.key,
        );
        if (matchingProvision) {
          formattedProvision[data?.eg_nid] = matchingProvision?.value;
          // formattedProvision.tooltip[data?.eg_nid] =
          //   matchingProvision?.evidence?.[0] || '';
          formattedProvision.background[
            data?.eg_nid
          ] = getProvisonCellBackground(
            matchingProvision?.favorability_range,
            matchingProvision?.value,
          );
        }
      });
    }

    return formattedProvision;
  });

  return formattedData;
};

export const formatFees = (
  fees: Fee[],
  compareFees: {
    eg_nid: string;
    fees: Fee[];
  }[],
  onClickReference?: any,
) => {
  if (!fees?.length) {
    return [];
  }

  //Sort fees per rules
  const sortedFees = [
    // Administrative Services (ASO) always first
    ...fees.filter((f) => f?.field_name === 'Administrative Services (ASO)'),
    // Fees with value, sorted alphabetically
    ...fees
      .filter(
        (f) =>
          f?.field_name !== 'Administrative Services (ASO)' &&
          f?.value &&
          f?.value !== '-',
      )
      .sort((a, b) =>
        a?.field_name?.localeCompare(b?.field_name, undefined, {
          sensitivity: 'base',
        }),
      ),
    // Fees without value, sorted alphabetically
    ...fees
      .filter(
        (f) =>
          f?.field_name !== 'Administrative Services (ASO)' &&
          (!f?.value || f?.value === '-'),
      )
      .sort((a, b) =>
        a?.field_name?.localeCompare(b?.field_name, undefined, {
          sensitivity: 'base',
        }),
      ),
  ];

  // Map through the provisions of the current group entries
  const formattedData = sortedFees?.map((fee) => {
    const formattedFees: Record<string, any> = {
      name: fee?.field_name,
      benchmark: fee?.favorability_range,
      value: fee?.value,
      // tooltip: {
      //   description: fee?.field_description,
      //   value: fee?.evidence[0],
      // },
      background: {
        value: getProvisonCellBackground(fee?.favorability_range, fee?.value),
      },
      onClickReference,
    };

    if (compareFees?.length) {
      // Dynamically add values from each eg_nid in allData
      compareFees?.forEach((data) => {
        const matchingFee = data?.fees?.find((p) => p?.key === fee?.key);
        if (matchingFee) {
          formattedFees[data?.eg_nid] = matchingFee?.value;
          // formattedFees.tooltip[data?.eg_nid] = matchingFee?.evidence[0] || '';
          formattedFees.background[data?.eg_nid] = getProvisonCellBackground(
            matchingFee?.favorability_range,
            matchingFee?.value,
          );
        }
      });
    }

    return formattedFees;
  });

  return formattedData;
};

export const abbreviate = (text: string) => {
  return text
    .replace(/\([^)]*\)|\b\w\b/g, '')
    .split(' ')
    .map((word) => word.charAt(0))
    .join('');
};

export const extractCompareAgreementDetails = (
  data: any,
  report: string,
  isDraft?: boolean,
) => {
  let compareAgreementId: string = '';
  let compareUrl: string = '';
  let compareContract: any = {};
  let compareBaseDocument: any = {};

  // Define criteria for each report type
  const reportCriteria: Record<string, { agreementNames: string[] }> = {
    'Comparison with Stop Loss Agreement': {
      agreementNames: ['Stop-Loss Insurance Provider'],
    },
    'Comparison with Administrative Service Agreement': {
      agreementNames: [
        'Third-Party Administrator (TPA)',
        'Administrative Services Only (ASO) Provider',
      ],
    },
  };

  const criteria = reportCriteria[report];

  if (criteria) {
    const matchesAgreementName = (name: string) =>
      criteria?.agreementNames?.includes(name);

    compareContract = isDraft
      ? data?.find(
          (cont: any) =>
            matchesAgreementName(cont?.service_agreement_name) &&
            cont?.document_type === 'Base Document',
        )
      : data?.find(
          (cont: any) =>
            matchesAgreementName(cont?.service_agreement_name) &&
            cont.merge_status === 'Merged' &&
            cont?.type === 'Base Document',
        );

    compareBaseDocument = isDraft
      ? data?.find((doc: any) => doc?.document_type === 'Base Document')
      : compareContract?.documents?.find(
          (doc: any) => doc?.document_type === 'Base Document',
        );

    compareAgreementId = isDraft
      ? compareContract?.draft_id
      : compareContract?.contract_id || '';
    compareUrl = compareBaseDocument?.document_url || '';
  }

  return {
    compareAgreementId,
    compareUrl,
  };
};

export const formatPlanDocsReports = (data: any) => {
  const reportList = data?.map((plan: any) => {
    const {
      selectedReports,
      asaContract,
      planDoc,
      stoplossContract,
      pbmContract,
    } = plan;

    const planReports: any = {
      plan_id: planDoc?._id,
      reports: [],
    };

    selectedReports?.map((report: string) => {
      const reportObj: any = {
        report: report,
      };
      switch (report) {
        case 'Comparison with Administrative Service Agreement':
          reportObj.compare_agreement_id =
            asaContract?.contract_id || undefined;
          const compareUrl = asaContract?.documents?.find(
            (item: any) =>
              item?.document_type === 'Base Document' ||
              item?.document_type === 'Master Document',
          )?.document_url;
          reportObj.compareUrl = compareUrl;
          break;
        case 'Gap Analysis':
          reportObj.compare_agreement_id =
            stoplossContract?.contract_id || undefined;
          const slCompareUrl = stoplossContract?.documents?.find(
            (item: any) =>
              item?.document_type === 'Base Document' ||
              item?.document_type === 'Master Document',
          )?.document_url;
          reportObj.compareUrl = slCompareUrl;
          break;
        case 'Comparison with PBM Service Agreement':
          reportObj.compare_agreement_id =
            pbmContract?.contract_id || undefined;
          const pbmCompareUrl = pbmContract?.documents?.find(
            (item: any) =>
              item?.document_type === 'Base Document' ||
              item?.document_type === 'Master Document',
          )?.document_url;
          reportObj.compareUrl = pbmCompareUrl;
          break;
      }
      planReports.reports.push(reportObj);
    });

    return planReports;
  });

  return reportList;
};

export const formatContractReports = (
  data: any,
  allContracts: any,
  isDraft?: boolean,
) => {
  const reportTypeMap: Record<string, string[]> = {
    'Comparison with Stop Loss Agreement': ['Stop-Loss Insurance Provider'],
    'Comparison with Administrative Service Agreement': [
      'Third-Party Administrator (TPA)',
      'Administrative Services Only (ASO) Provider',
    ],
  };

  const reportList = data?.map((item: any) => {
    const extraFeatures =
      item?.type === 'Third-Party Administrator (TPA)' ||
      item?.type === 'Administrative Services Only (ASO) Provider'
        ? ['Key Terms Compilation']
        : [];

    const baseFeatures = [
      ...getFeaturesByServiceAgreement(item?.type),
      ...extraFeatures,
      'Red Flags Check',
      'Missing Checks',
    ];

    const features = [
      ...item?.selectedFeatures.filter((feature: string) =>
        baseFeatures.includes(feature),
      ),
      ...extraFeatures,
      'Red Flags Check',
      'Missing Checks',
    ];
    const reports = item?.selectedFeatures.filter(
      (feature: string) => !baseFeatures.includes(feature),
    );

    const contractReports = reports?.map((report: string) => {
      const contractTypes = reportTypeMap[report];

      let compareAgreementId: string | undefined = undefined;

      if (contractTypes?.length) {
        const matchingContract = allContracts.find((cont: any) =>
          contractTypes.includes(cont?.type),
        );
        compareAgreementId = isDraft
          ? matchingContract?.draft_id
          : matchingContract?.contract_id;
      }

      return {
        report,
        compare_agreement_id: compareAgreementId,
      };
    });

    return {
      ...(isDraft
        ? { draft_id: item?.draft_id }
        : { contract_id: item?.contract_id }),
      reports: contractReports,
      features,
    };
  });

  return reportList;
};

export const getCoverageCellBackground = (
  status: string,
  value: boolean | string,
) => {
  switch (status) {
    case 'Favourable':
      return value === true ? '#08C7B41A' : '#EDC0BB4D';
    case 'Unfavourable':
      return value === true ? '#EDC0BB4D' : '#08C7B41A';
    default:
      return '';
  }
};

export const getPlanDocReports = async (
  egNid: string,
  docType: 'SPD' | 'SOB',
) => {
  if (docType === 'SOB') {
    return [];
  }
  const reports: string[] = [
    'ERISA Regulatory Compliance Check',
    'Overview',
    'Exclusion Analysis',
  ];

  const response = await axios.post(
    `${properties.aiqBaseUrl}${properties.contractBaseUrl}${properties.getContracts}`,
    { eg_nid: egNid },
  );
  if (response.status !== 200) {
    throw new Error('Failed to fetch contracts');
  }
  const contracts = response.data?.data;
  const asaContract = contracts?.find(
    (cont: any) =>
      (cont?.service_agreement_name === 'Third-Party Administrator (TPA)' ||
        cont?.service_agreement_name ===
          'Administrative Services Only (ASO) Provider') &&
      cont.merge_status === 'Merged',
  );
  if (asaContract) {
    reports.push('Comparison with Administrative Service Agreement');
  }

  const stoplossContract = contracts?.find(
    (cont: any) =>
      cont?.service_agreement_name === 'Stop-Loss Insurance Provider' &&
      cont.merge_status === 'Merged',
  );
  if (stoplossContract) {
    reports.push('Gap Analysis');
  }
  const pbmContract = contracts?.find(
    (cont: any) =>
      cont?.service_agreement_name === 'Pharmacy Benefit Manager (PBM)' &&
      cont.merge_status === 'Merged',
  );
  if (pbmContract) {
    reports.push('Comparison with PBM Service Agreement');
  }

  return reports;
};

export const getFeatureTabName = (label: string) => {
  switch (label) {
    case 'Red Flag Checks':
      return 'Red Flags';
    case 'Missing Checks':
      return 'Missing Provisions';
    case 'Summary of Findings':
      return 'Summary';
    default:
      return label;
  }
};

export const getCategoryByFeature = (label: string) => {
  switch (label) {
    case 'Key Terms Compilation':
      return 'Key Terms';
    case 'Red Flags Check':
      return 'Red Flag Checks';
    case 'Missing Checks':
      return 'Missing Checks';
    default:
      return label;
  }
};

export const getOrderedTabsByAgreement = (agreement: string) => {
  switch (agreement) {
    case 'Third-Party Administrator (TPA)':
    case 'Administrative Services Only (ASO) Provider':
      return contractAIQConstants.ASO_CONTRACT_TABS;
    case 'Stop-Loss Insurance Provider':
      return [
        'Key Terms',
        'Red Flags',
        'Missing Provisions',
        'Compliance Analysis',
      ];
    case 'Direct Contract Provider (or Group)':
      return [
        'Red Flags',
        'Missing Provisions',
        'Gag Clause',
        'Compliance Analysis',
      ];
    default:
      return contractAIQConstants.ASO_CONTRACT_TABS;
  }
};

export const formatFeesProvisions = (fees: Fee[], onClickReference: any) => {
  const sortedFees = [
    // Administrative Services (ASO) always first
    ...fees.filter(
      (f) => f?.provision_name === 'Administrative Services (ASO)',
    ),
    // Fees with value, sorted alphabetically
    ...fees
      .filter(
        (f) =>
          f?.provision_name !== 'Administrative Services (ASO)' &&
          f?.value &&
          f?.value !== '-',
      )
      .sort((a, b) =>
        a?.provision_name?.localeCompare(b?.provision_name, undefined, {
          sensitivity: 'base',
        }),
      ),
    // Fees without value, sorted alphabetically
    ...fees
      .filter(
        (f) =>
          f?.provision_name !== 'Administrative Services (ASO)' &&
          (!f?.value || f?.value === '-'),
      )
      .sort((a, b) =>
        a?.provision_name?.localeCompare(b?.provision_name, undefined, {
          sensitivity: 'base',
        }),
      ),
  ];

  return sortedFees?.map((fee) => ({
    ...fee,
    provision: fee?.provision_name,
    benchmark: fee?.benchmark,
    value: (
      <Typography fontSize="14px" lineHeight="24px" component="div">
        <span>{fee?.value}</span>
        {fee?.chunks?.length > 0 &&
          Array.from(
            new Map(
              fee?.chunks?.map((ref: any) => {
                const key =
                  ref?.grounding?.extractions?.reference ??
                  ref?.grounding?.page;

                return [key, ref];
              }),
            ).values(),
          ).map((ref: any, ind: number, arr: any[]) => {
            const reference = ref?.grounding?.extractions?.reference;
            const page = ref?.grounding?.page;
            if (reference || (page !== null && page !== undefined)) {
              return (
                <DottedText
                  key={ref.chunk_id}
                  text={
                    reference
                      ? `${reference}`
                      : `PgNo:${page + 1}${ind < arr.length - 1 ? ',' : ''}`
                  }
                  styles={{ paddingLeft: '5px' }}
                  onClick={() => onClickReference(fee.chunks, page)}
                />
              );
            }
            return null;
          })}
      </Typography>
    ),
    tooltip: {
      description: fee?.assessment_description,
      value: fee?.evidence[0],
    },
    background: {
      value: getProvisonCellBackground(fee?.favorability, fee?.value),
    },
  }));
};

export const formatNewProvisions = (provisions: MatrixData[]) => {
  return provisions?.map((provision) => ({
    ...provision,
    provision: provision?.provision_name,
    status: provision?.favorability,
    value: provision.value,
    category: provision?.clause,
    provisionsCategory: provision?.category,
    clause: provision?.clause,
    impact: provision?.impact,
    importance: provision?.importance,
    tooltip: {
      description: provision?.assessment_description,
      explaination: provision?.explanation,
      value: provision?.evidence?.[0] || '',
    },
    background: {
      value: getProvisonCellBackground(
        provision?.favorability,
        provision?.value,
      ),
    },
  }));
};

export const formatMatrixData = (
  data: any[],
  category: string,
  filters: any,
) => {
  if (data === undefined || data === null || !Array.isArray(data)) {
    return [];
  }
  const pageMap = new Map<number | null, any>();

  const matchesSelectedFilter = (clause: string) =>
    !filters?.selectedFilters?.length ||
    filters.selectedFilters.includes(clause?.trim());

  const matchesImportanceFilter = (importance: string) =>
    !filters?.importanceFilters?.length ||
    filters.importanceFilters.includes(importance);

  const matchesLegalFilter = (temp: any) => {
    if (!filters?.legalFilters?.length) {
      return true;
    }

    if (filters.legalTypeFilter === 'Legal Doctrine') {
      const doctrines = temp?.legal_doctorine || temp?.legal_doctrine || [];
      return doctrines?.some((d: string) => filters.legalFilters.includes(d));
    }

    if (filters.legalTypeFilter === 'Legal Citation') {
      const citations = temp?.legal_citation || [];
      return citations?.some((c: string) => filters.legalFilters.includes(c));
    }

    return true;
  };

  data?.forEach((item) => {
    let provisions: any[] = [];

    if (category === 'Red Flag Checks') {
      provisions = item?.redFlags?.filter((temp: any) => {
        const flaggedCondition =
          filters?.flaggedOnly || filters?.flaggedOnly === null
            ? temp?.value === true
            : true;

        return (
          flaggedCondition &&
          matchesSelectedFilter(temp?.clause) &&
          matchesImportanceFilter(temp?.importance) &&
          matchesLegalFilter(temp)
        );
      });
    }

    if (category === 'Missing Checks') {
      provisions = item?.missingProvisions?.filter((temp: any) => {
        const missingCondition =
          filters?.missingOnly || filters?.missingOnly === null
            ? temp?.value !== true
            : true;

        return (
          missingCondition &&
          matchesSelectedFilter(temp?.clause) &&
          matchesImportanceFilter(temp?.importance) &&
          matchesLegalFilter(temp)
        );
      });
    }

    provisions?.forEach((prov: any) => {
      // ---- Page handling (ALLOW missing) ----
      const page = prov?.evidence?.page ?? item?.page ?? null; // 👈 explicitly allow null
      const reference = prov?.evidence?.extractions?.reference;
      const extractionTopic = prov?.evidence?.extractions?.topic?.trim();
      let topic = '';
      if (extractionTopic) {
        const base = reference
          ? `${reference} - ${extractionTopic}`
          : extractionTopic;

        topic = page ? `Pg ${page + 1} : ${base}` : base;
      } else {
        topic =
          item?.topic?.trim() ||
          prov?.clause?.trim() ||
          (page ? `Pg ${page + 1}` : '');
      }

      // ---- Page bucket ----
      if (!pageMap.has(page)) {
        pageMap.set(page, {
          page,
          chunks: [],
        });
      }

      const pageEntry = pageMap.get(page);

      // ---- Chunk bucket (NO DUPLICATES per page) ----

      let chunk = pageEntry.chunks.find((c: any) => c.chunkName === topic);

      if (!chunk) {
        chunk = {
          chunkName: topic,
          provisions: [],
          groundingData: [],
        };
        pageEntry.chunks.push(chunk);
      }

      // ---- Provision mapping ----
      chunk.provisions.push({
        title: prov.provision_name,
        importance: prov.importance,
        description: prov.explanation,
        check: prov?.check,
        legalDoctrine: prov?.legal_doctorine || prov?.legal_doctrine || [],
        legalCitation: prov?.legal_citation || [],
      });

      // ---- Grounding mapping (ONLY if evidence exists) ----
      if (prov?.evidence?.chunk_id) {
        const exists = chunk.groundingData.some(
          (g: any) => g.chunk_id === prov.evidence.chunk_id,
        );

        if (!exists) {
          chunk.groundingData.push({
            chunk_id: prov.evidence.chunk_id,
            grounding: {
              box: prov.evidence.box,
              page: prov.evidence.page,
              type: 'chunkText',
              extractions: prov.evidence.extractions,
            },
          });
        }
      }
    });
  });

  // ---- SORT pages (null last) ----
  return Array.from(pageMap.values()).sort((a, b) => {
    if (a.page === null) {
      return 1;
    }
    if (b.page === null) {
      return -1;
    }
    return a.page - b.page;
  });
};
